import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ViewContainerRef,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ButtonsModule,
  CardModule,
  CheckboxModule,
  LoadingService,
} from 'axis-components';

import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RequestService } from '../../../_services/request/request.service';
import { StateSummaryService } from '../../../_services/uim-ui/state-summary.service';
import { UserService } from '../../../_services/user.service';
import { FormType, RequestProgressStep } from '../../../config/constants';
import { UimuiBaseRulesService } from '../../../state-disclosures/uimui/services/uimui-base-rules.service';
import { UimuiBaseStateComponent } from '../../../state-disclosures/uimui/uimui-base-state.component';
import { BrowserNavigationService } from '../../../_services/browser-navigation.service';

@Component({
  selector: 'app-uimui-state-summary',
  templateUrl: './uimui-state-summary.component.html',
  styleUrl: './uimui-state-summary.component.scss',
  standalone: true,
  imports: [
    ButtonsModule,
    CardModule,
    CheckboxModule,
    CommonModule,
    ReactiveFormsModule,
  ],
})
export class UimuiStateSummaryComponent implements AfterViewInit, OnInit {
  public unStateSummary: FormGroup;
  @ViewChild('stateComponent', { read: ViewContainerRef })
  stateComponent!: ViewContainerRef;
  @ViewChild('summaryContainer', { read: ElementRef })
  summaryContainer!: ElementRef;
  public baseRulesService = inject(UimuiBaseRulesService);
  public baseStateComponent = UimuiBaseStateComponent;

  // Events emitted to playground component for workflow navigation
  @Output() continueClickedPlayground = new EventEmitter<void>();
  @Output() backClickedPlayground = new EventEmitter<void>();
  isBroker = false;

  stateSummaryData: any;
  constructor(
    private readonly stateSummaryService: StateSummaryService,
    private router: Router,
    private fb: FormBuilder,
    private requestService: RequestService,
    private userService: UserService,
    private loadingService: LoadingService,
    private cdr: ChangeDetectorRef,
    private browserNavigationService: BrowserNavigationService,
  ) {}
  ngOnInit(): void {
    // Reset loading state when component initializes
    this.browserNavigationService.startListening({
      onBackNavigation: () => UimuiBaseStateComponent.resetLoadingState(),
    });
    UimuiBaseStateComponent.resetLoadingState();
    this.loadingService.start('State Summary is Loading');
    this.isBroker = this.userService.role === 'broker';
    this.stateSummaryService.getStateSummaryData().subscribe((data) => {
      this.stateSummaryData = data;
    });
    this.unStateSummary = this.fb.group({
      undoConfirmStateDis: [''],
    });

    // Restore checkbox state from dataDump (checkbox retention)
    const savedCheckbox =
      this.baseRulesService.dataDump?.['summaryConfirmation'];
    if (savedCheckbox !== undefined) {
      this.unStateSummary.patchValue({ undoConfirmStateDis: savedCheckbox });
    }

    // Subscribe to form changes to save checkbox state
    this.unStateSummary.valueChanges.subscribe(() => {
      this.saveCheckboxState();
    });

    // Set up validation function for sidebar navigation
    this.baseRulesService.canNavigateFromSummary = () => {
      return this.validateSummaryForm();
    };

    this.browserNavigationService.stopListening();
  }

  ngAfterViewInit(): void {
    this.setupDynamicComponents();
  }
  setupDynamicComponents() {
    let applicableStates = this.baseRulesService.getApplicableStates();
    applicableStates.forEach(({ state, stateComponent }) => {
      const stateComponentRef =
        this.stateComponent.createComponent(stateComponent);
      (stateComponentRef.location.nativeElement as HTMLElement).classList.add(
        'state-wrapper',
      );

      (stateComponentRef.instance as any).isReview = true;
    });
    // this is temporary solution until we get all the json compiled into 1 json for all states from backend
    this.waitForFinalRender();
    // this is temporary solution until we get all the json compiled into 1 json for all states from backend
    setTimeout(() => {
      console.log('Is state page empty : ', this.isStatePageEmptyScoped);
    }, 1000);
  }
  private waitForFinalRender(): void {
    const checkUIState = () => {
      if (UimuiBaseStateComponent.stopLoading) {
        this.loadingService.stop();
        return;
      } else {
        this.cdr.detectChanges();
        requestAnimationFrame(checkUIState);
      }
    };
    requestAnimationFrame(checkUIState);
  }

  get isStatePageEmptyScoped(): boolean {
    if (this.summaryContainer?.nativeElement) {
      return this.baseRulesService.isStatePageEmpty(
        this.summaryContainer.nativeElement,
      );
    }
    return this.baseRulesService.isStatePageEmpty();
  }

  goTop(): void {
    // reset the page when changing route
    window.scrollTo(0, 0);
  }
  onBack(): void {
    // Reset loading state when going back
    UimuiBaseStateComponent.resetLoadingState();
    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.backClickedPlayground.emit();
      return;
    }
    this.router.navigate(['/insured/uimui-selection'], {
      queryParamsHandling: 'preserve',
    });
  }
  onContinue(): void {
    // Validate the summary form
    if (!this.validateSummaryForm()) {
      return;
    }

    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.continueClickedPlayground.emit();
      return;
    }
    this.requestService.refreshRequestData(false).subscribe({
      next: () => {
        this.handleContinueWithFreshStep();
      },
      error: () => {
        this.handleContinueWithFreshStep();
      },
    });
  }

  private handleContinueWithFreshStep(): void {
    // Read current DB-backed step snapshot before moving the form ahead.
    const currentFormStep = this.requestService.getStepForFormType(
      FormType.UM_UIM,
    );
    const isUimuiCompleted = this.requestService.isFormTypeAlreadyCompleted(
      FormType.UM_UIM,
    );

    // If UM/UIM is already completed, do not continue with consent routing.
    if (isUimuiCompleted) {
      return;
    }

    this.requestService.updateRequestProgress(
      FormType.UM_UIM,
      RequestProgressStep.REVIEW_STATE_SELECTIONS,
      this.baseRulesService.dataDump,
    );

    this.router.navigate(['/insured/uimui-outstanding-state'], {
      queryParamsHandling: 'preserve',
    });
  }

  private validateSummaryForm(): boolean {
    const isEmpty = this.isStatePageEmptyScoped;
    const confirmControl = this.unStateSummary.get('undoConfirmStateDis');

    if (isEmpty || this.isBroker) {
      // If page is empty or user is broker, remove validators and clear the control
      confirmControl?.clearValidators();
      confirmControl?.setValue('');
    } else {
      // If page has content and user is not broker, require the checkbox
      confirmControl?.setValidators([Validators.requiredTrue]);
    }

    // Update validation state
    confirmControl?.updateValueAndValidity();
    confirmControl?.markAsTouched();

    if (this.unStateSummary.invalid && !isEmpty && !this.isBroker) {
      console.log('Form is invalid');
      return false;
    }

    return true;
  }

  /**
   * Save checkbox state to dataDump for persistence
   */
  private saveCheckboxState(): void {
    const checkboxValue = this.unStateSummary.get('undoConfirmStateDis')?.value;
    // Initialize dataDump as object if needed (it can be array or object)
    if (
      !this.baseRulesService.dataDump ||
      Array.isArray(this.baseRulesService.dataDump)
    ) {
      this.baseRulesService.dataDump = this.baseRulesService.dataDump || [];
    }
    this.baseRulesService.dataDump['summaryConfirmation'] = checkboxValue;
  }
}
